import Sidebar from '@/components/Sidebar/SidebarView.vue';
import Navbar from '@/components/HomeNavBar/HomeNavBar.vue';
import { get_token, get_user_details } from "@/router";
import InactivityModal from '@/components/InactivityTracker/InactivityTracker.vue';
import GetCommunityAssets from '@/components/GetCommunityAssets/CommunityAsset.vue';
import Swal from 'sweetalert2';

export default {
  components: {
    Sidebar,
    Navbar,
    InactivityModal,
    GetCommunityAssets,
  },

  data() {
    return ({
      is_loading: false,
      community_assets: [],
      asset_dashboard: {},

      current_user_id: null,
      current_role: '',
      token_exists: false,
    });
  },

  computed: {
    asset_dashboard_cards() {
      return [
        { label: 'Total Assets', value: this.get_asset_dashboard_value('total_count', this.community_assets.length), icon: 'bi-box-seam-fill', tone: 'primary' },
        { label: 'Categories', value: this.get_asset_dashboard_value('category_count'), icon: 'bi-tags-fill', tone: 'info' },
        { label: 'Creators', value: this.get_asset_dashboard_value('creator_count'), icon: 'bi-people-fill', tone: 'success' },
        { label: 'With Fees', value: this.get_asset_dashboard_value('assets_with_subscription_fee'), icon: 'bi-cash-stack', tone: 'warning' },
        { label: 'Booked', value: this.get_asset_dashboard_value('booked_assets'), icon: 'bi-calendar-check-fill', tone: 'danger' },
        { label: 'Bookings', value: this.get_asset_dashboard_value('total_bookings'), icon: 'bi-calendar2-week-fill', tone: 'info' },
      ];
    },
  },

  mounted() {
    this.token_exists = !!this.get_token();
    try {
      const user = get_user_details();
      this.current_user_id = parseInt(user?.user_id, 10) || null;
      this.current_role = user?.user_role || '';
    } catch (_) {
      this.current_user_id = null;
      this.current_role = '';
    }

    this.fetch_community_assets();
  },

  methods: {
    get_token() {
      return get_token();
    },

    get_asset_dashboard_value(key, fallback = 0) {
      const value = Reflect.get(this.asset_dashboard || {}, key);
      return [undefined, null, ''].includes(value) ? fallback : value;
    },

    refresh_asset_dashboard_from_list() {
      const assets = this.community_assets || [];
      const categories = new Set();

      assets.forEach((asset) => {
        const category = this.get_asset_category_key(asset);

        if (category) categories.add(String(category));
      });

      this.asset_dashboard = {
        ...this.asset_dashboard,
        total_count: assets.length,
        category_count: categories.size,
        booked_assets: assets.filter(this.is_asset_booked).length,
        assets_with_files: assets.filter(this.asset_has_files.bind(this)).length,
        total_files: assets.reduce((total, asset) => total + this.get_asset_file_count(asset), 0),
      };
    },

    get_asset_category_key(asset = {}) {
      const category = Reflect.get(asset, 'category') || {};
      return Reflect.get(category, 'id') || Reflect.get(category, 'name');
    },

    get_asset_file_count(asset = {}) {
      const files = Reflect.get(asset, 'asset_files') || Reflect.get(asset, 'files') || [];
      return files.length || 0;
    },

    is_asset_booked(asset = {}) {
      return Boolean(Reflect.get(asset, 'is_booked'));
    },

    asset_has_files(asset = {}) {
      return this.get_asset_file_count(asset) > 0;
    },

    // -- Fetch list (no DataTables calls here; global script handles table init)
    fetch_community_assets() {
      this.is_loading = true;
      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/get_community_asset/`,
        method: 'POST',
        headers: {
          'Authorization': `Token ${this.get_token()}`,
          'Content-Type': 'application/json'
        },
        success: (data) => {
          this.community_assets = Array.isArray(data?.community_assets) ? data.community_assets : [];
          this.asset_dashboard = data?.dashboard || {};
        },
        error: () => {
          this.community_assets = [];
          this.asset_dashboard = {};
        },
        complete: () => {
          this.is_loading = false;
        }
      });
    },

    // -- Actions (delegate to child where appropriate)
    select_asset(asset) {
      this.$refs.communityAssets?.select_asset(asset);
    },

    edit_asset(asset) {
      this.$refs.communityAssets?.edit_asset(asset);
    },
    asset_bookings(asset){
      this.$refs.communityAssets?.view_bookings(asset)
    },
    delete_asset(asset_id, evt) {
      if (evt) {
        evt.stopPropagation?.();
        evt.stopImmediatePropagation?.();
      }

      Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
        position: 'bottom-end',
        toast: true
      }).then((result) => {
        if (!result.isConfirmed) return;

        $.ajax({
          type: "DELETE",
          url: `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/delete_community_asset/`,
          data: { community_asset_id: asset_id },
          headers: { 'Authorization': `Token ${this.get_token()}` },
          success: (response) => {
            if (response.status === "success") {
              Swal.fire({
                title: 'Deleted!',
                text: 'The Asset has been deleted.',
                icon: 'success',
                showConfirmButton: false,
                timer: 1000,
                position: 'bottom-end',
                toast: true
              });
              // remove locally; global datatable script will observe DOM and adjust
              this.community_assets = this.community_assets.filter(a => a.id !== asset_id);
              this.refresh_asset_dashboard_from_list();
              // optional: refresh child list if it maintains its own copy
              this.$refs.communityAssets?.fetchcommunity_assets?.();
            } else {
              Swal.fire({
                title: 'Failed!',
                text: response.message || 'An error occurred. Please try again.',
                icon: 'error',
                position: 'bottom-end',
                showConfirmButton: false,
                timer: 3000,
                toast: true
              });
            }
          },
          error: (xhr) => {
            const resp = xhr.responseJSON || {};
            Swal.fire({
              title: 'Failed!',
              text: resp.message || 'An error occurred. Please try again.',
              icon: 'error',
              position: 'bottom-end',
              showConfirmButton: false,
              timer: 3000,
              toast: true
            });
          }
        });
      });
    },

    book_asset(asset) {
      if (this.$refs.communityAssets?.openBookingModal) {
        this.$refs.communityAssets.openBookingModal(asset);
      } else if (this.$refs.communityAssets?.view_bookings) {
        this.$refs.communityAssets.view_bookings(asset);
      }
    },

    toggle_sidebar() {
      this.$refs.sidebar?.$el.classList.toggle('close');
    },

    // ---------- (Optional) compatibility aliases ----------
    fetchcommunity_assets() { return this.fetch_community_assets(); },
    toggleSidebar() { return this.toggle_sidebar(); },
  },
};
